import type { AxiosResponse } from 'axios'
import type { AuthResponse, LoginCredentials, ResetPasswordResponse } from '../types/auth'
import { API_ENDPOINTS } from '../utils/apiEndpoints'
import { logError } from '../utils/sentry'
import type { ApiRequestConfig } from './api'
import api from './api'

export async function authenticate(credentials: LoginCredentials): Promise<AuthResponse | null> {
  try {
    const response: AxiosResponse<AuthResponse> = await api.post(API_ENDPOINTS.AUTH.LOGIN, credentials)

    if (response.data) {
      try {
        await api.get('/brands')
      } catch (err) {
        logError('Failed to fetch brand data after login without user ID', { error: err })
      }
      return response.data
    }

    logError('Authentication response missing accessToken')
    return null
  } catch (error) {
    logError('Authentication error:', { error })
    throw error
  }
}

const authService = {
  login: async (email: string, password: string): Promise<AuthResponse | null> => {
    return authenticate({ email, password })
  },

  refreshToken: async (_refreshToken: string): Promise<string | null> => {
    try {
      const response: AxiosResponse<AuthResponse> = await api.post(API_ENDPOINTS.AUTH.REFRESH)

      if (response.status === 200) return ''

      return null
    } catch (error) {
      console.error('Refresh token error: ', error)
      throw error
    }
  },

  logout: async (): Promise<void> => {
    try {
      await api.post(API_ENDPOINTS.AUTH.LOGOUT)
    } catch (error) {
      console.warn('Logout backend error:', error)
    }
  },

  resetPassword: async (email: string): Promise<number | null> => {
    try {
      const response: AxiosResponse<ResetPasswordResponse> = await api.post(API_ENDPOINTS.AUTH.RESET_PASSWORD, {
        email,
      })
      return response.status
    } catch (error) {
      console.error('Reset password error: ', error)
      throw error
    }
  },

  verifyOTP: async (email: string, verificationCode: string): Promise<number | null> => {
    try {
      const response: AxiosResponse<ResetPasswordResponse> = await api.post(API_ENDPOINTS.AUTH.VERIFY_OTP, {
        email,
        verificationCode,
      })

      return response.status
    } catch (error) {
      console.error('Verify OTP error: ', error)
      throw error
    }
  },

  verifyRegistrationOTP: async (email: string, verificationCode: string): Promise<ResetPasswordResponse> => {
    try {
      const response: AxiosResponse<ResetPasswordResponse> = await api.post(
        API_ENDPOINTS.AUTH.BRAND_VERIFY,
        {
          email,
          verificationCode,
        },
        { skipAuthToast: true } as ApiRequestConfig,
      )

      return response.data
    } catch (error) {
      console.error('Verify registration OTP error: ', error)
      throw error
    }
  },

  createNewPassword: async (newPassword: string, confirmNewPassword: string): Promise<number | null> => {
    try {
      const response: AxiosResponse<ResetPasswordResponse> = await api.post(API_ENDPOINTS.AUTH.CREATE_NEW_PASSWORD, {
        newPassword,
        confirmNewPassword,
      })
      return response.status
    } catch (error) {
      console.error('Create new password error: ', error)
      throw error
    }
  },
}

export default authService
